import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { toast } from "sonner";
import MobileShell from "@/components/admin/MobileShell";
import { api, formatDate } from "@/lib/api";
import { BulkPurgeBar } from "@/pages/AdminDashboard";

const FILTERS = [
  { id: "all", label: "All", value: undefined },
  { id: "sent", label: "Pending", value: "sent" },
  { id: "viewed", label: "Viewed", value: "viewed" },
  { id: "signed", label: "Signed", value: "signed" },
  { id: "expired", label: "Expired", value: "expired" },
];

export default function AdminContracts() {
  const [filter, setFilter] = useState("all");
  const [contracts, setContracts] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [purging, setPurging] = useState(false);

  const load = () => {
    const f = FILTERS.find((x) => x.id === filter);
    api.get("/admin/contracts", { params: { status_filter: f?.value } }).then(({ data }) => setContracts(data || []));
  };

  useEffect(() => { setSelected(new Set()); load(); /* eslint-disable-next-line */ }, [filter]);

  const toggle = (clientId) => {
    if (!clientId) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId); else next.add(clientId);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const visibleClientIds = contracts.map((c) => c.client_id).filter(Boolean);
  const allSelected = visibleClientIds.length > 0 && visibleClientIds.every((id) => selected.has(id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        visibleClientIds.forEach((id) => next.delete(id));
      } else {
        visibleClientIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const bulkPurge = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/clients/bulk-purge", { client_ids: ids });
      toast.success(`Deleted ${data.clients_deleted ?? ids.length} client${(data.clients_deleted ?? ids.length) === 1 ? "" : "s"} permanently.`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete clients.");
    } finally {
      setPurging(false);
    }
  };

  return (
    <MobileShell title="Contracts">
      <h1 className="font-display text-2xl text-[#0A1930] tracking-tight mt-2">Contracts</h1>
      <p className="text-sm text-slate-500 mt-1 mb-4">All service agreements at a glance.</p>

      <div className="-mx-4 px-4 flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {FILTERS.map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`shrink-0 rounded-full border px-4 py-2 text-sm ${filter === f.id ? "bg-[#0A1930] text-white border-[#0A1930]" : "bg-white text-[#0A1930] border-slate-200"}`}
            data-testid={`contracts-filter-${f.id}`}>
            {f.label}
          </button>
        ))}
      </div>

      {visibleClientIds.length > 0 && (
        <div className="flex items-center justify-between px-1 mb-3" data-testid="contracts-select-all-row">
          <label className="inline-flex items-center gap-2 text-xs text-slate-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAllVisible}
              className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
              data-testid="contracts-select-all-checkbox"
            />
            <span>Select all {visibleClientIds.length}</span>
          </label>
          {selected.size > 0 && (
            <button onClick={clearSelection} className="text-xs text-slate-500 hover:underline" data-testid="contracts-clear-selection-btn">
              Clear ({selected.size})
            </button>
          )}
        </div>
      )}

      <div className="space-y-2 pb-24">
        {contracts.length === 0 && <div className="text-center text-sm text-slate-500 py-10">No contracts.</div>}
        {contracts.map((c) => {
          const checked = c.client_id ? selected.has(c.client_id) : false;
          return (
            <Link key={c.contract_id} to={`/admin/clients/${c.client_id}`}
              className={`block bg-white border rounded-2xl p-3 pl-2 hover:border-slate-300 transition-all ${checked ? "border-rose-300 bg-rose-50/50" : "border-slate-200"}`}
              data-testid={`contracts-row-${c.contract_id}`}>
              <div className="flex items-center gap-2">
                <span
                  className="shrink-0 flex items-center justify-center w-9 h-9 -ml-1"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(c.client_id); }}
                  data-testid={`contracts-row-check-${c.contract_id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    readOnly
                    className="h-5 w-5 rounded border-slate-300 text-rose-600 focus:ring-rose-500 pointer-events-none"
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-[#0A1930] truncate">{c.client?.full_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{c.client?.branch} · {c.fee_percentage}% · Sent {formatDate(c.created_at)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{c.status}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {selected.size > 0 && (
        <BulkPurgeBar
          count={selected.size}
          onConfirm={bulkPurge}
          purging={purging}
          onClear={clearSelection}
        />
      )}
    </MobileShell>
  );
}
